<script setup lang="ts">
import { onMounted, ref } from "vue";
import { useRouter } from "vue-router";

import DropdownButton from "@/components/ui/DropdownButton.vue";
import DropdownButtonItem from "@/components/ui/DropdownButtonItem.vue";
import SkoreSpinner from "@/components/ui/SkoreSpinner.vue";
import { type WorkspaceDto } from "@/dto";
import DefaultPageLayout from "@/layouts/DefaultPageLayout.vue";
import { RouteNames } from "@/router";
import { useApi } from "@/services/api";
import { formatDatetime } from "@/services/utils";

const api = useApi();
const router = useRouter();
const isLoading = ref(true);
const workspaces = ref<WorkspaceDto[]>([]);

async function refreshWorkspaces() {
  isLoading.value = true;
  workspaces.value = await api.listWorkspaces();
  if (workspaces.value.length > 0) {
    isLoading.value = false;
  } else {
    router.push({ name: RouteNames.Onboarding });
  }
}

function onEdit(workspaceId: number) {
  router.push({ name: RouteNames.WorkspaceDetail, params: { workspaceId } });
}

onMounted(async () => {
  await refreshWorkspaces();
  if (workspaces.value.length == 1) {
    const firstWorkspace = workspaces.value[0];
    if (firstWorkspace) {
      router.replace({ name: RouteNames.Projects, params: { workspaceName: firstWorkspace.name } });
    }
  }
});
</script>

<template>
  <DefaultPageLayout :breadcrumb-items="[{ label: 'Workspaces' }]">
    <div class="workspaces-list-view">
      <Transition name="fade" mode="out-in">
        <div v-if="isLoading" class="loading">
          <SkoreSpinner />
        </div>
        <div v-else class="wrapper">
          <header>
            <div class="text-2xl-medium">Workspaces</div>
            <div class="text-base-regular">Browse the workspaces you have access to.</div>
          </header>
          <table class="tabular-list">
            <thead>
              <tr>
                <th class="text-base-regular">Workspace name</th>
                <th class="text-base-regular">Members</th>
                <th class="text-base-regular">Created</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="workspace in workspaces" :key="workspace.id">
                <td>
                  <RouterLink
                    :to="{
                      name: RouteNames.Projects,
                      params: { workspaceName: workspace.name },
                    }"
                  >
                    <span class="name text-base-regular">
                      {{ workspace.name }}
                    </span>
                  </RouterLink>
                </td>
                <td>{{ workspace.members?.length ?? 0 }}</td>
                <td class="cell-with-actions">
                  <div>{{ formatDatetime(workspace.created_at, false) }}</div>
                  <DropdownButton icon-right="icon-more-vertical" kind="ghost">
                    <DropdownButtonItem
                      label="Edit"
                      icon-left="icon-edit"
                      @click="onEdit(workspace.id)"
                    />
                  </DropdownButton>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Transition>
    </div>
  </DefaultPageLayout>
</template>

<style scoped>
.workspaces-list-view {
  .loading {
    margin-top: 40dvh;
    text-align: center;
  }

  .wrapper {
    display: flex;
    flex-direction: column;
    padding: calc(var(--numbers-32) - var(--numbers-12)) 0 var(--numbers-32) 0;
    gap: var(--numbers-32);

    header {
      div {
        color: var(--content-secondary);
      }
    }

    .cell-with-actions {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
    }

    a {
      text-decoration: none;
    }

    thead {
      tr {
        color: var(--content-secondary);
      }
    }

    tbody {
      tr {
        color: var(--content-tertiary);
      }
    }

    .name {
      --icon-size: var(--numbers-24);

      position: relative;
      padding-left: calc(var(--icon-size) + var(--numbers-8));
      color: var(--content-primary);

      &::before {
        position: absolute;
        top: -35%;
        left: 0;
        overflow: hidden;
        width: var(--icon-size);
        height: var(--icon-size);
        border: solid var(--stroke-width-md) var(--border-tertiary);
        border-radius: var(--icon-size);
        background-image: url("@/assets/images/skore-icon.svg");
        background-position: 60% 50%;
        background-repeat: no-repeat;
        background-size: 70%;
        content: "";
      }
    }
  }
}
</style>
